/**
 * <sort-dropdown> — custom listbox for the collection sort control.
 *
 * The native <select name="sort_by"> stays in the form (hidden) so FacetFiltersForm's
 * FormData still picks up the value; selecting an option writes to it and dispatches a
 * bubbling `input` event, which is what facets.js listens for on the form.
 *
 * Progressive enhancement: without JS the native select renders and works as normal.
 */
class SortDropdown extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('.cl-sort__input');
    this.toggle = this.querySelector('.cl-sort__toggle');
    this.panel = this.querySelector('.cl-sort__panel');
    this.valueEl = this.querySelector('.cl-sort__value');
    if (!this.select || !this.toggle || !this.panel) return;

    this.options = Array.from(this.panel.querySelectorAll('[role="option"]'));
    this.activeIndex = this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    if (this.activeIndex < 0) this.activeIndex = 0;

    // Hand control to the custom UI.
    this.select.setAttribute('tabindex', '-1');
    this.select.setAttribute('aria-hidden', 'true');
    this.toggle.hidden = false;
    this.setAttribute('data-ready', '');

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.toggle.addEventListener('click', this.onToggleClick.bind(this));
    this.panel.addEventListener('click', this.onPanelClick.bind(this));
    // The bottom-sheet scrim is this element's ::after, so a tap on it reports the host
    this.addEventListener('click', (event) => {
      if (this.isOpen && event.target === this) this.close();
    });
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    // A filter change re-renders this element; never leave a portalled sheet behind
    clearTimeout(this.closeTimer);
    this.portalClose();
  }

  get isOpen() {
    return this.toggle.getAttribute('aria-expanded') === 'true';
  }

  onToggleClick() {
    this.isOpen ? this.close() : this.open();
  }

  onDocumentClick(event) {
    if (this.isOpen && !this.contains(event.target)) this.close();
  }

  onPanelClick(event) {
    const option = event.target.closest('[role="option"]');
    if (option) {
      this.choose(this.options.indexOf(option));
      return;
    }

    // Below 750px the panel is a bottom sheet whose ::before is the full-screen scrim.
    // A pseudo-element is never an event target, so a tap on the scrim reports the panel.
    if (event.target === this.panel) this.close();
  }

  get isSheet() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  onKeyDown(event) {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ', 'Escape', 'Tab'];
    if (!keys.includes(event.key)) return;

    if (event.key === 'Tab') {
      if (this.isOpen) this.close(false);
      return;
    }

    if (event.key === 'Escape') {
      if (this.isOpen) {
        event.preventDefault();
        this.close();
      }
      return;
    }

    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.open();
      }
      return;
    }

    event.preventDefault();

    if (event.key === 'Enter' || event.key === ' ') {
      this.choose(this.activeIndex);
      return;
    }

    let next = this.activeIndex;
    if (event.key === 'ArrowDown') next = Math.min(this.activeIndex + 1, this.options.length - 1);
    if (event.key === 'ArrowUp') next = Math.max(this.activeIndex - 1, 0);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.options.length - 1;
    this.setActive(next);
  }

  /**
   * Below 750px the panel is a bottom sheet, which needs position: fixed against the
   * viewport. It cannot get that where it sits: #ProductGridContainer carries Dawn's
   * .animate--slide-in, and that animation ends `forwards` on transform: translateY(0),
   * so the container stays the containing block for fixed descendants forever — the sheet
   * would dock to the bottom of the product grid instead of the screen.
   * So while it is open the panel is moved to <body>, inside a layer that repeats
   * .collection-grid-wrapper + the color-scheme class so its scoped CSS still applies.
   */
  portalOpen() {
    if (this.layer) return;

    const source = this.closest('.collection-grid-wrapper');
    const scoped = source
      ? Array.from(source.classList).filter((name) => name === 'collection-grid-wrapper' || name.startsWith('color-'))
      : ['collection-grid-wrapper'];

    this.layer = document.createElement('div');
    this.layer.className = `${scoped.join(' ')} cl-sort-sheet__layer`;

    this.scrim = document.createElement('div');
    this.scrim.className = 'cl-sort-sheet__scrim';
    this.scrim.addEventListener('click', () => this.close());

    this.panelHome = document.createComment('cl-sort-panel');
    this.panel.before(this.panelHome);
    this.layer.append(this.scrim, this.panel);
    document.body.appendChild(this.layer);
  }

  portalClose() {
    if (!this.layer) return;

    // Put the panel back where it came from, unless facets.js re-rendered that markup away
    if (this.panelHome && this.panelHome.isConnected) {
      this.panelHome.replaceWith(this.panel);
    } else {
      this.panel.remove();
    }

    this.layer.remove();
    this.layer = null;
    this.scrim = null;
    this.panelHome = null;
  }

  open() {
    // Cancel a close that is still animating out, otherwise it would hide the sheet again
    clearTimeout(this.closeTimer);
    this.panel.classList.remove('is-closing');
    if (this.isSheet) this.portalOpen();
    this.panel.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.setActive(this.activeIndex);
  }

  close(returnFocus = true) {
    this.toggle.setAttribute('aria-expanded', 'false');
    this.panel.removeAttribute('aria-activedescendant');

    // The sheet slides back down before it is hidden; the popover just disappears.
    if (this.layer && !this.panel.hidden) {
      this.panel.classList.add('is-closing');
      if (this.scrim) this.scrim.classList.add('is-closing');
      const hide = () => {
        clearTimeout(this.closeTimer);
        this.panel.classList.remove('is-closing');
        this.panel.hidden = true;
        this.portalClose();
      };
      this.panel.addEventListener('animationend', hide, { once: true });
      this.closeTimer = setTimeout(hide, 300);
    } else {
      this.panel.hidden = true;
      this.portalClose();
    }

    if (returnFocus) this.toggle.focus();
  }

  setActive(index) {
    this.activeIndex = index;
    this.options.forEach((option, i) => option.classList.toggle('is-active', i === index));
    const active = this.options[index];
    if (!active) return;
    this.panel.setAttribute('aria-activedescendant', active.id);
    // Only scroll when the panel itself overflows — otherwise this scrolls the page,
    // which on the bottom sheet looked like the page jumping down on open
    if (this.panel.scrollHeight > this.panel.clientHeight) active.scrollIntoView({ block: 'nearest' });
  }

  choose(index) {
    const option = this.options[index];
    if (!option) return;

    this.options.forEach((item, i) => {
      item.setAttribute('aria-selected', i === index ? 'true' : 'false');
      item.classList.toggle('is-selected', i === index);
    });
    if (this.valueEl) this.valueEl.textContent = option.dataset.label || option.textContent.trim();

    this.close();

    if (this.select.value === option.dataset.value) return;
    this.select.value = option.dataset.value;
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

if (!customElements.get('sort-dropdown')) {
  customElements.define('sort-dropdown', SortDropdown);
}
